import React from 'react';
import { Plus, Edit, Trash2, Package, Store, Sparkles } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

const InternalShopList = ({
  shops,
  loading,
  onAddShop,
  onEditShop,
  onDeleteShop,
  onManageProducts
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  if (loading) {
    return (
      <div className="relative group">
        {/* Glass Background */}
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 backdrop-blur-2xl border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
          : 'bg-white border border-slate-200 shadow-sm'
        } rounded-3xl`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
          : ''
        } rounded-3xl`}></div>

        {/* Glass Particles */}
        <div className={`absolute top-6 right-8 w-2 h-2 ${isDarkMode ? 'bg-white/20 rounded-full animate-pulse' : 'hidden'}`}></div>

        <div className="relative p-8">
          <div className="flex flex-col items-center justify-center py-12">
            <div className={`animate-spin rounded-full h-10 w-10 border-3 ${isDarkMode
              ? 'border-white/20 border-t-white/80'
              : 'border-slate-200 border-t-brand'
            } mb-4`}></div>
            <p className={`${isDarkMode ? 'text-white' : 'text-slate-900'} font-medium`}>{t('admin.internalShops.loading')}</p>
            <p className={`${isDarkMode ? 'text-white/60' : 'text-slate-600'} mt-1`}>{t('common.pleaseWait')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (shops.length === 0) {
    return (
      <div className="relative group">
        {/* Glass Background */}
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 backdrop-blur-2xl border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
          : 'bg-white border border-slate-200 shadow-sm'
        } rounded-3xl`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
          : ''
        } rounded-3xl`}></div>

        {/* Glass Particles */}
        <div className={`absolute top-6 right-8 w-2 h-2 ${isDarkMode ? 'bg-white/20 rounded-full animate-pulse' : 'hidden'}`}></div>

        <div className="relative p-8">
          <div className="flex flex-col items-center justify-center py-12">
            {/* Liquid Glass Empty State Icon */}
            <div className="relative group/empty mb-6">
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-3xl blur-sm'
                : 'hidden'
              }`}></div>
              <div className={`relative w-16 h-16 rounded-3xl flex items-center justify-center ${isDarkMode
                ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20'
                : 'bg-brand'
              }`}>
                <Store className={`w-8 h-8 ${isDarkMode ? 'text-white/90' : 'text-white'}`} />
                <div className={`absolute top-2 left-2 w-4 h-4 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
              </div>
            </div>

            <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-3`}>{t('admin.internalShops.empty')}</h3>
            <p className={`${isDarkMode ? 'text-white/70' : 'text-slate-600'} text-center max-w-sm mb-8`}>
              {t('admin.internalShops.emptyDesc')}
            </p>
            
            {/* Liquid Glass CTA Button */}
            <button
              onClick={onAddShop}
              className="relative group/cta overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99]"
              style={{
                background: isDarkMode
                  ? 'linear-gradient(135deg, rgba(59,130,246,0.7) 0%, rgba(37,99,235,0.7) 50%, rgba(29,78,216,0.7) 100%)'
                  : '#2563EB',
                backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
                border: isDarkMode ? '1px solid rgba(255,255,255,0.3)' : 'none',
                boxShadow: isDarkMode
                  ? '0 8px 32px rgba(59,130,246,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  : 'none'
              }}
            >
              {/* Smooth Hover Overlay */}
              <div
                className="absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/cta:opacity-100"
                style={{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                    : 'rgba(255,255,255,0.12)'
                }}
              ></div>

              <div className="relative z-10 px-6 py-3 flex items-center font-medium text-white transition-all duration-300 group-hover/cta:drop-shadow-xl">
                <Plus className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/cta:scale-110" />
                {t('admin.internalShops.createFirst')}
              </div>

              {/* Smooth Wave Effect */}
              <div className="absolute inset-0 opacity-0 group-hover/cta:opacity-100 transition-opacity duration-500">
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/cta:translate-x-full transition-transform duration-800"
                  style={{
                    background: isDarkMode
                      ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                      : 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                  }}
                ></div>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative group">
      {/* Glass Background */}
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 backdrop-blur-2xl border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
        : 'bg-white border border-slate-200 shadow-sm'
      } rounded-3xl`}></div>
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
        : ''
      } rounded-3xl`}></div>

      {/* Glass Particles */}
      <div className={`absolute top-6 right-8 w-2 h-2 ${isDarkMode ? 'bg-white/20 rounded-full animate-pulse' : 'hidden'}`}></div>
      
      <div className="relative p-6">
        <div className="space-y-4">
          {shops.map((shop) => (
            <ShopCard
              key={shop.id}
              shop={shop}
              isDarkMode={isDarkMode}
              onEdit={() => onEditShop(shop)}
              onDelete={() => onDeleteShop(shop)}
              onManageProducts={() => onManageProducts({ ...shop, type: 'internal' })}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const ShopCard = ({ shop, isDarkMode, onEdit, onDelete, onManageProducts }) => {
  const { t } = useLanguage();
  return (
    <div className="relative group/item">
      {/* Shop Card Glass Background */}
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-br from-gray-900/20 via-gray-800/15 to-gray-900/20 backdrop-blur-xl border border-white/20 shadow-[0_4px_16px_rgba(0,0,0,0.2)] group-hover/item:shadow-[0_8px_24px_rgba(0,0,0,0.3)]'
        : 'bg-white border border-slate-200 shadow-sm group-hover/item:shadow-md'
      } rounded-2xl transition-all duration-300`}></div>
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10 opacity-0 group-hover/item:opacity-100'
        : 'hidden'
      } rounded-2xl transition-opacity duration-300`}></div>
      
      <div className="relative p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Liquid Glass Shop Icon */}
            <div className="relative group/shopicon">
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm'
                : 'hidden'
              }`}></div>
              <div className={`relative w-12 h-12 rounded-2xl flex items-center justify-center ${isDarkMode
                ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20'
                : 'bg-brand'
              }`}>
                {shop.icon ? (
                  <span className="text-2xl relative z-10">{shop.icon}</span>
                ) : (
                  <Store className={`w-6 h-6 ${isDarkMode ? 'text-white/90' : 'text-white'} relative z-10`} />
                )}
                <div className={`absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
              </div>
            </div>
            
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{shop.name}</h4>

                {/* Liquid Glass Status Badge */}
                <span className={`relative inline-flex items-center px-3 py-1 text-xs font-medium rounded-full border ${
                  shop.is_active
                    ? isDarkMode
                      ? 'bg-emerald-500/20 border-emerald-400/30 text-emerald-200 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]'
                      : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                    : isDarkMode
                      ? 'bg-red-500/20 border-red-400/30 text-red-200 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]'
                      : 'bg-red-50 border-red-200 text-red-700'
                }`}>
                  {shop.is_active ? t('common.active') : t('common.inactive')}
                </span>

                {shop.category && (
                  <span className={`relative inline-flex items-center px-3 py-1 text-xs font-medium rounded-full border ${isDarkMode
                    ? 'bg-white/10 border-white/20 text-white/80 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]'
                    : 'bg-slate-100 border-slate-200 text-slate-700'
                  }`}>
                    {shop.category}
                  </span>
                )}
              </div>

              {shop.description && (
                <p className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-slate-600'} mb-2`}>{shop.description}</p>
              )}

              <div className={`flex items-center space-x-4 text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                <span className="flex items-center">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {t('admin.internalShops.internalCatalog')}
                </span>
                <span>Shop-ID: {shop.id}</span>
              </div>
            </div>
          </div>
          
          {/* Liquid Glass Action Buttons */}
          <div className="flex items-center space-x-2">
            <ActionButton
              onClick={onManageProducts}
              icon={Package}
              title={t('admin.products.title')}
              isDarkMode={isDarkMode}
              hoverColor="emerald"
            />
            <ActionButton
              onClick={onEdit}
              icon={Edit}
              title={t('common.edit')}
              isDarkMode={isDarkMode}
              hoverColor="blue"
            />
            <ActionButton
              onClick={onDelete}
              icon={Trash2}
              title={t('admin.internalShops.deleteShop')}
              isDarkMode={isDarkMode}
              hoverColor="red"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ActionButton = ({ onClick, icon: Icon, title, isDarkMode, hoverColor }) => {
  const getHoverColors = () => {
    switch(hoverColor) {
      case 'emerald':
        return {
          gradient: isDarkMode
            ? 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.2) 50%, rgba(16,185,129,0.15) 100%)'
            : 'rgba(209,250,229,0.8)',
          textColor: isDarkMode ? 'text-emerald-300' : 'text-emerald-700',
          waveColor: isDarkMode
            ? 'linear-gradient(90deg, transparent 0%, rgba(16,185,129,0.2) 50%, transparent 100%)'
            : 'transparent'
        };
      case 'blue':
        return {
          gradient: isDarkMode
            ? 'linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.2) 50%, rgba(59,130,246,0.15) 100%)'
            : 'rgba(219,234,254,0.8)',
          textColor: isDarkMode ? 'text-blue-300' : 'text-brand',
          waveColor: isDarkMode
            ? 'linear-gradient(90deg, transparent 0%, rgba(59,130,246,0.2) 50%, transparent 100%)'
            : 'transparent'
        };
      case 'red':
        return {
          gradient: isDarkMode
            ? 'linear-gradient(135deg, rgba(239,68,68,0.15) 0%, rgba(220,38,38,0.2) 50%, rgba(239,68,68,0.15) 100%)'
            : 'rgba(254,226,226,0.8)',
          textColor: isDarkMode ? 'text-red-300' : 'text-red-600',
          waveColor: isDarkMode
            ? 'linear-gradient(90deg, transparent 0%, rgba(239,68,68,0.2) 50%, transparent 100%)'
            : 'transparent'
        };
      default:
        return {
          gradient: '',
          textColor: '',
          waveColor: ''
        };
    }
  };

  const colors = getHoverColors();

  return (
    <button
      onClick={onClick}
      className={`relative group/${hoverColor} overflow-hidden rounded-xl transition-all duration-300 transform hover:scale-[1.05] active:scale-[0.95]`}
      title={title}
      style={{
        background: isDarkMode
          ? 'linear-gradient(135deg, rgba(71,85,105,0.4) 0%, rgba(75,85,99,0.4) 50%, rgba(71,85,105,0.4) 100%)'
          : '#F1F5F9',
        backdropFilter: isDarkMode ? 'blur(16px)' : 'none',
        border: isDarkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid #E2E8F0',
        boxShadow: isDarkMode
          ? '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
          : 'none'
      }}
    >
      {/* Smooth Hover Overlay */}
      <div 
        className={`absolute inset-0 rounded-xl transition-all duration-300 opacity-0 group-hover/${hoverColor}:opacity-100`}
        style={{ background: colors.gradient }}
      ></div>
      
      <div className="relative p-2">
        <Icon className={`w-4 h-4 transition-all duration-300 ${
          isDarkMode ? `text-white/70 group-hover/${hoverColor}:${colors.textColor}` : `text-slate-600 group-hover/${hoverColor}:${colors.textColor}`
        } group-hover/${hoverColor}:scale-110`} />
      </div>
      
      {/* Smooth Wave Effect */}
      <div className={`absolute inset-0 opacity-0 group-hover/${hoverColor}:opacity-100 transition-opacity duration-400`}>
        <div 
          className={`absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/${hoverColor}:translate-x-full transition-transform duration-600`}
          style={{ background: colors.waveColor }}
        ></div>
      </div>
    </button>
  );
};

export default InternalShopList;